// src/pages/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ModuleCard from '../components/ModuleCard';
import AdminPanel from '../components/AdminPanel';
import SettingsModal from './SettingsModal';
import TaskBoard from '../components/TaskBoard';
import OnlineUsers from '../components/OnlineUsers';
import ChatWidget from '../components/ChatWidget';
import PaymentRequiredModal from '../components/PaymentRequiredModal';
import {
  Briefcase, Settings, LogOut, BarChart3, GraduationCap,
  Download, X, Check, FileText, Wallet,
  Sparkles,
} from 'lucide-react';
import api from '../api/client';
import PortalSwitch from '../components/PortalSwitch';

interface Module {
  id: string;
  title: string;
  description: string;
  imageSrc: string;
  icon?: string;
  path: string;
  requiresPermission?: string;
  requiresHRM?: boolean;
}

// ================= Кастомное уведомление о копировании =================
const CopyNotification: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <div style={copyNotificationStyles.overlay} onClick={onClose}>
      <div style={copyNotificationStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={copyNotificationStyles.iconWrapper}>
          <Check size={28} color="#28a745" />
        </div>
        <h4 style={copyNotificationStyles.title}>Адрес скопирован!</h4>
        <p style={copyNotificationStyles.message}>
          Вставьте <strong>chrome://extensions</strong> в адресную строку и нажмите Enter.
        </p>
        <button onClick={onClose} style={copyNotificationStyles.button}>
          Понятно
        </button>
      </div>
    </div>
  );
};

const copyNotificationStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 11000,
  },
  modal: {
    background: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '28px',
    width: '380px',
    maxWidth: '90%',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.3) inset',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  iconWrapper: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  message: {
    fontSize: '14px',
    color: '#555',
    margin: '0 0 20px 0',
    lineHeight: 1.5,
  },
  button: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '30px',
    padding: '10px 28px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(37,117,252,0.3)',
    transition: 'all 0.2s ease',
  },
};

// ================= Модальное окно плагина =================
const PluginInstallModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  if (!isOpen) return null;

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, isPrimary = true) => {
    const target = e.currentTarget;
    target.style.transform = 'scale(1.02)';
    target.style.boxShadow = '0 8px 20px rgba(37,117,252,0.4)';
    if (isPrimary) {
      target.style.background = 'linear-gradient(135deg, #5a0db8, #1a5fd9)';
    } else {
      target.style.background = 'rgba(37,117,252,0.1)';
      target.style.borderColor = '#2575fc';
      target.style.color = '#1a1a2e';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, isPrimary = true) => {
    const target = e.currentTarget;
    target.style.transform = 'scale(1)';
    target.style.boxShadow = isPrimary ? '0 4px 12px rgba(37,117,252,0.3)' : 'none';
    if (isPrimary) {
      target.style.background = 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)';
    } else {
      target.style.background = 'transparent';
      target.style.borderColor = '#2575fc';
      target.style.color = '#2575fc';
    }
  };

  const handleCloseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'rgba(37,117,252,0.1)';
    e.currentTarget.style.color = '#2575fc';
  };
  const handleCloseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.5)';
    e.currentTarget.style.color = '#4a4a68';
  };

  const handleCopyExtensionsLink = (url = 'chrome://extensions') => {
    navigator.clipboard.writeText(url);
    setShowCopyNotification(true);
  };

  return (
    <>
      <div style={pluginModalStyles.overlay} onClick={onClose}>
        <div style={pluginModalStyles.modal} onClick={e => e.stopPropagation()}>
          <div style={pluginModalStyles.header}>
            <h3 style={pluginModalStyles.title}>Расширение для браузера</h3>
            <button
              onClick={onClose}
              style={pluginModalStyles.closeButton}
              onMouseEnter={handleCloseEnter}
              onMouseLeave={handleCloseLeave}
            >
              <X size={20} />
            </button>
          </div>

          <div style={pluginModalStyles.content}>
            <h4 style={pluginModalStyles.sectionTitle}>📥 Установка</h4>

            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>1</div>
              <div>
                <p style={pluginModalStyles.stepText}>Скачайте файл расширения для своего браузера:</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <a href="/downloads/hrm-extension-chrome.zip" download style={pluginModalStyles.downloadButton}
                     onMouseEnter={(e) => handleMouseEnter(e, true)}
                     onMouseLeave={(e) => handleMouseLeave(e, true)}>
                    <Download size={16} style={{ marginRight: 6 }} /> 🌐 Chrome
                  </a>
                  <a href="/downloads/hrm-extension-yandex.zip" download style={pluginModalStyles.downloadButton}
                     onMouseEnter={(e) => handleMouseEnter(e, true)}
                     onMouseLeave={(e) => handleMouseLeave(e, true)}>
                    <Download size={16} style={{ marginRight: 6 }} /> 🟡 Яндекс.Браузер
                  </a>
                  <a href="/downloads/hrm-extension-chrome.zip" download style={pluginModalStyles.downloadButton}
                     onMouseEnter={(e) => handleMouseEnter(e, true)}
                     onMouseLeave={(e) => handleMouseLeave(e, true)}>
                    <Download size={16} style={{ marginRight: 6 }} /> 🔷 Edge / Opera
                  </a>
                </div>
              </div>
            </div>

            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>2</div>
              <div>
                <p style={pluginModalStyles.stepText}>Откройте страницу управления расширениями для своего браузера:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, fontSize: 13 }}>
                  <div>
                    🌐 Chrome:{' '}
                    <span style={pluginModalStyles.link} onClick={() => handleCopyExtensionsLink('chrome://extensions')} title="Кликните чтобы скопировать">
                      chrome://extensions
                    </span>
                  </div>
                  <div>
                    🟡 Яндекс.Браузер:{' '}
                    <span style={pluginModalStyles.link} onClick={() => handleCopyExtensionsLink('browser://extensions')} title="Кликните чтобы скопировать">
                      browser://extensions
                    </span>
                    {' '}(или <strong>Меню → Настройки → Расширения</strong>)
                  </div>
                  <div>
                    🔷 Edge:{' '}
                    <span style={pluginModalStyles.link} onClick={() => handleCopyExtensionsLink('edge://extensions')} title="Кликните чтобы скопировать">
                      edge://extensions
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>3</div>
              <p style={pluginModalStyles.stepText}>
                Включите <strong>«Режим разработчика»</strong> (переключатель в правом верхнем углу).
              </p>
            </div>

            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>4</div>
              <div>
                <p style={pluginModalStyles.stepText}>Установите расширение:</p>
                <ul style={pluginModalStyles.list}>
                  <li>Распакуйте скачанный <strong>.zip</strong>-архив в любую папку.</li>
                  <li>В Chrome/Edge нажмите <strong>«Загрузить распакованное расширение»</strong> и выберите папку.</li>
                  <li>В Яндекс.Браузере нажмите <strong>«Загрузить расширение»</strong> и выберите папку.</li>
                </ul>
              </div>
            </div>

            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>5</div>
              <p style={pluginModalStyles.stepText}>
                Готово! Расширение появится в панели браузера.
              </p>
            </div>

            <div style={pluginModalStyles.divider} />

            <h4 style={pluginModalStyles.sectionTitle}>🚀 Как пользоваться</h4>
            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>1</div>
              <p style={pluginModalStyles.stepText}>
                Откройте резюме или вакансию на <strong>hh.ru</strong>, <strong>superjob.ru</strong> или <strong>avito.ru</strong>.
              </p>
            </div>
            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>2</div>
              <p style={pluginModalStyles.stepText}>
                Нажмите на иконку расширения <span style={{ fontWeight: 600, color: '#2575fc' }}>«HRM Lama Pro»</span>.
              </p>
            </div>
            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>3</div>
              <div>
                <p style={pluginModalStyles.stepText}>Выберите действие:</p>
                <ul style={pluginModalStyles.list}>
                  <li><strong>👤 Кандидата</strong> — сохранить резюме в HRM</li>
                  <li><strong>💼 Вакансию</strong> — сохранить вакансию в HRM</li>
                </ul>
              </div>
            </div>
            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>4</div>
              <p style={pluginModalStyles.stepText}>
                При совпадении с существующими записями можно заменить данные или создать дубликат.
              </p>
            </div>
            <div style={pluginModalStyles.step}>
              <div style={pluginModalStyles.stepNumber}>5</div>
              <p style={pluginModalStyles.stepText}>
                Данные мгновенно появятся в HRM.
              </p>
            </div>

            <div style={pluginModalStyles.note}>
              <p style={pluginModalStyles.noteText}>
                🔐 Расширение работает при открытой HRM в соседней вкладке — <code style={pluginModalStyles.code}>https://hrlama.ru</code>.

                ⚙️ Поддерживается Chrome 90+, Яндекс.Браузер 22+, Edge 90+, Opera 76+.

                При проблемах обращайтесь в поддержку через Тикеты на главном дашборде.
              </p>
            </div>
          </div>

          <div style={pluginModalStyles.footer}>
            <button onClick={onClose} style={pluginModalStyles.closeActionButton}
                    onMouseEnter={(e) => handleMouseEnter(e, false)}
                    onMouseLeave={(e) => handleMouseLeave(e, false)}>
              Закрыть
            </button>
          </div>
        </div>
      </div>

      {showCopyNotification && (
        <CopyNotification onClose={() => setShowCopyNotification(false)} />
      )}
    </>
  );
};

const pluginModalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
  },
  modal: {
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: '32px',
    padding: '32px',
    width: '650px',
    maxWidth: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 25px 45px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.3) inset',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#1a1a2e',
    margin: 0,
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  closeButton: {
    background: 'rgba(255,255,255,0.5)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#4a4a68',
    backdropFilter: 'blur(4px)',
    transition: 'all 0.2s ease',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a1a2e',
    margin: '0 0 4px 0',
  },
  step: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
  },
  stepNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '14px',
    flexShrink: 0,
  },
  stepText: {
    fontSize: '15px',
    color: '#333',
    margin: 0,
    lineHeight: 1.5,
  },
  list: {
    margin: '8px 0 0 0',
    paddingLeft: '20px',
    fontSize: '14px',
    color: '#555',
    lineHeight: 1.6,
  },
  link: {
    color: '#2575fc',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'color 0.2s',
    background: 'rgba(37,117,252,0.05)',
    padding: '0 4px',
    borderRadius: '4px',
  },
  code: {
    background: 'rgba(37,117,252,0.1)',
    padding: '2px 8px',
    borderRadius: '6px',
    fontFamily: 'monospace',
    fontSize: '13px',
    fontWeight: 500,
    color: '#2575fc',
  },
  downloadButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '12px',
    flexWrap: 'wrap',
  },
  downloadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '10px 20px',
    borderRadius: '30px',
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(37,117,252,0.3)',
    transition: 'all 0.2s ease',
  },
  divider: {
    height: '1px',
    background: 'rgba(0,0,0,0.08)',
    margin: '8px 0',
  },
  note: {
    background: 'rgba(37,117,252,0.07)',
    borderRadius: '16px',
    padding: '14px 18px',
    border: '1px solid rgba(37,117,252,0.15)',
    marginTop: '8px',
  },
  noteText: {
    fontSize: '14px',
    color: '#333',
    margin: 0,
  },
  footer: {
    marginTop: '28px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeActionButton: {
    background: 'transparent',
    border: '1px solid #2575fc',
    borderRadius: '40px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#2575fc',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};

// ================= Основной компонент дашборда =================
const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const roleLabel = { superadmin: 'Супер Админ', admin: 'Администратор', hr_manager: 'HR-менеджер', recruiter: 'Рекрутер', employee: 'Сотрудник', user: 'Пользователь', sales_manager: 'Менеджер продаж', accountant: 'Бухгалтер', kdp: 'КДП' }[user?.role || 'user'] || user?.role || '';
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const [unreadTickets, setUnreadTickets] = useState(0);

  // ★ Счётчик непрочитанных тикетов для badge на кнопке Настройки
  const fetchUnreadTickets = React.useCallback(async () => {
    try {
      const res = await api.get('/tickets/my');
      const ticketsData = res.data || [];
      let count = 0;
      ticketsData.forEach((t: any) => {
        const lastAdminReply = (t.replies || [])
          .filter((r: any) => r.from === 'admin')
          .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
        if (!lastAdminReply) return;
        const lastSeen = localStorage.getItem(`ticket_${t.id}_lastSeen`) || t.createdAt;
        if (new Date(lastAdminReply.timestamp) > new Date(lastSeen)) count++;
      });
      setUnreadTickets(count);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnreadTickets();
    const interval = setInterval(fetchUnreadTickets, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadTickets]);

  useEffect(() => {
    if (!showSettings) fetchUnreadTickets();
  }, [showSettings, fetchUnreadTickets]);
  const [isTaskBoardOpen, setIsTaskBoardOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [isTablet, setIsTablet] = useState(window.innerWidth < 1400);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [showPluginModal, setShowPluginModal] = useState(false); // новое состояние

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings');
        const savedState = res.data.dashboardLayout?.taskboardOpen;
        if (savedState !== undefined) setIsTaskBoardOpen(savedState);
      } catch (err) {
        console.error('Ошибка загрузки настроек', err);
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSettings();
  }, []);

  const toggleTaskBoard = async () => {
    const newState = !isTaskBoardOpen;
    setIsTaskBoardOpen(newState);
    try {
      await api.put('/settings', { dashboardLayout: { taskboardOpen: newState } });
    } catch (err) {
      console.error('Ошибка сохранения настроек', err);
      setIsTaskBoardOpen(!newState);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1200);
      setIsTablet(width < 1400);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!user) {
    navigate('/login');
    return null;
  }

  const modules: Module[] = [
    {
      id: 'interview-assistant',
      title: 'Помощник в проведении интервью',
      description: 'Оценка навыков и определение мотиваторов',
      icon: '🤝',
      imageSrc: '/images/module-icons/п.png',
      path: '/app/interview-assistant',
    },
    {
      id: 'recruitment-analytics',
      title: 'Data-Driven Recruitment',
      description: 'Аналитика воронки найма, метрики и рекомендации',
      icon: '📈',
      imageSrc: '/images/module-icons/дата.png',
      path: '/app/recruitment-analytics',
      requiresPermission: 'canViewDataDriven',
      requiresHRM: true,
    },
    {
      id: 'compatibility',
      title: 'Проверка соответствия',
      description: 'Оцените, насколько кандидат подходит вакансии',
      icon: '✓',
      imageSrc: '/images/module-icons/кандидат.png',
      path: '/app/compatibility',
    },
    {
      id: 'kpi-profiles',
      title: 'KPI Конструктор',
      description: 'Библиотека KPI по профилям деятельности с возможностью расчёта',
      icon: '📈',
      imageSrc: '/images/module-icons/kpi.png',
      path: '/app/kpi',
    },
    {
      id: 'hr-metrics',
      title: 'HR Метрики',
      description: 'Справочник и калькулятор HR-метрик',
      icon: '📊',
      imageSrc: '/images/module-icons/метрик.png',
      path: '/app/hr-metrics',
    },
    {
      id: 'psychodiagnostics',
      title: 'Психодиагностика',
      description: 'Профессиональные психологические тесты из книги Райгородского',
      icon: '🧪',
      imageSrc: '/images/module-icons/псих.png',
      path: '/app/psychodiagnostics',
    },
    {
      id: 'psych-tests',
      title: 'Психологические тесты',
      description: 'Оценка личности, мотивации, эмоционального интеллекта',
      icon: '🧠',
      imageSrc: '/images/module-icons/мозг.png',
      path: '/app/psych-tests',
    },
    {
      id: 'disc-test',
      title: 'DISC: Поведенческий профиль',
      description: 'Определите свой тип личности по методике DISC',
      icon: '📊',
      imageSrc: '/images/module-icons/disc.png',
      path: '/app/disc-test',
    },
    {
      id: 'hh-analytics',
      title: 'Аналитика зарплат',
      description: 'Поиск и анализ вакансий и резюме с HeadHunter',
      imageSrc: '/images/module-icons/Аналитика.png',
      path: '/app/hh-analytics',
    },
{
  id: 'hh-search',
  title: 'Поиск резюме с HH',
  description: 'Поиск вакансий и резюме по HeadHunter с фильтрами и выгрузкой в Excel',
  imageSrc: '/images/module-icons/hh1.png',
  path: '/app/hh-search',
},
    {
      id: 'pdf2word',
      title: 'Конвертер PDF в Word',
      description: 'Преобразуйте PDF в редактируемый документ',
      icon: '📑',
      imageSrc: '/images/module-icons/pdf-converter.png',
      path: '/app/pdf2word',
    },
    // ★ Скрытие модуля «Конструктор диаграмм» на проде
    ...(process.env.REACT_APP_HIDE_CHART_BUILDER === 'true' ? [] : [{
      id: 'charts',
      title: 'Конструктор диаграмм',
      description: 'Создавайте любые графики и воронки',
      imageSrc: '/images/module-icons/chart-builder.png',
      icon: '📊',
      path: '/app/charts',
    }]),
    {
      id: 'templates',
      title: 'Шаблоны документов',
      description: 'Загружайте и скачивайте шаблоны документов',
      icon: '📄',
      imageSrc: '/images/module-icons/док.png',
      path: '/app/templates',
    },
  ];

  const moduleColors = [
    'rgba(106, 17, 203, 0.08)',
    'rgba(37, 117, 252, 0.08)',
    'rgba(40, 167, 69, 0.08)',
    'rgba(255, 77, 79, 0.08)',
    'rgba(250, 173, 20, 0.08)',
  ];

  const isPaid = user.role === 'admin' || user.paid;

  const handleModuleClick = (path: string) => {
    if (!isPaid) {
      setShowPaymentModal(true);
      setPendingPath(path);
      return;
    }
    if (path === '#') alert('Модуль в разработке');
    else navigate(path);
  };

  const handleCrmClick = () => {
    if (!isPaid) {
      setShowPaymentModal(true);
      setPendingPath('/app/hr');
      return;
    }
    navigate('/app/hr');
  };

  const handleBiClick = () => {
    if (!isPaid) {
      setShowPaymentModal(true);
      setPendingPath('/app/bi');
      return;
    }
    navigate('/app/bi');
  };

  const handleElearningClick = () => {
    if (!isPaid) {
      setShowPaymentModal(true);
      setPendingPath('/app/elearning');
      return;
    }
    navigate('/app/elearning');
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
    setPendingPath(null);
  };

  if (loadingSettings) {
    return (
      <div style={styles.loadingWrapper}>
        <div style={styles.loading}>Загрузка...</div>
      </div>
    );
  }

  const showButtonText = !isTablet && !isMobile;

  const TooltipWrapper: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
    if (showButtonText) return <>{children}</>;
    return (
      <div style={styles.tooltipContainer} data-tooltip={title}>
        {children}
      </div>
    );
  };

  const getAnimatedButtonStyle = (baseColor: string): React.CSSProperties => ({
    ...styles.iconButton,
    borderColor: baseColor,
    color: baseColor,
    borderRadius: showButtonText ? '30px' : '50%',
    padding: showButtonText ? '6px 12px' : '6px',
    minWidth: showButtonText ? 'auto' : '38px',
    transition: 'all 0.3s ease',
  });

  return (
    <div style={styles.container}>
      {/* ═══ Портал — плавающая кнопка ═══ */}
      <PortalSwitch />
      <div style={styles.backgroundImage} />
      
      <header style={styles.header}>
        <div style={styles.logoContainer}>
          <img src="/images/20.png" alt="Лама" style={styles.logoIcon} />
          <span style={styles.logoText}></span>
        </div>
        <div style={styles.userInfo}>
          {!isMobile && <span style={styles.userName}>{user.name} ({roleLabel})</span>}
          <div style={{ position: 'relative', zIndex: 1000 }}>
            <OnlineUsers />
          </div>

          <TooltipWrapper title="BI Дашборды">
            <button
              onClick={handleBiClick}
              style={getAnimatedButtonStyle('#28a745')}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #28a745, #20c997)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = 'transparent';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#28a745';
                e.currentTarget.style.borderColor = '#28a745';
              }}
              title={showButtonText ? undefined : 'BI Дашборды'}
            >
              <BarChart3 size={16} />
              {showButtonText && <span style={{ marginLeft: '4px' }}>BI Дашборды</span>}
            </button>
          </TooltipWrapper>

          {/* Кнопка СРМ Продажи */}
          {((user as any)?.companyModules?.canViewSalesCRM !== false) && (user?.role === 'admin' || user?.role === 'superadmin' || (user as any)?.permissions?.canViewSalesCRM) && (
            <TooltipWrapper title="CRM">
              <button
                onClick={() => navigate('/app/sales')}
                style={getAnimatedButtonStyle('#2575fc')}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #2575fc, #6a11cb)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#2575fc';
                  e.currentTarget.style.borderColor = '#2575fc';
                }}
                title={showButtonText ? undefined : 'CRM'}
              >
                <Briefcase size={16} />
                {showButtonText && <span style={{ marginLeft: '4px' }}>CRM</span>}
              </button>
            </TooltipWrapper>
          )}

          {((user as any)?.companyModules?.canViewHRM !== false) && (user?.role === 'admin' || user?.role === 'superadmin' || (user as any)?.permissions?.canViewHRM) && (
          <TooltipWrapper title="HRM">
            <button
              onClick={handleCrmClick}
              style={getAnimatedButtonStyle('#2575fc')}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #2575fc, #6a11cb)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = 'transparent';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#2575fc';
                e.currentTarget.style.borderColor = '#2575fc';
              }}
              title={showButtonText ? undefined : 'HRM'}
            >
              <Briefcase size={16} />
              {showButtonText && <span style={{ marginLeft: '4px' }}>HRM</span>}
            </button>
          </TooltipWrapper>
          )}

          {/* Кнопка КДП */}
          {((user as any)?.companyModules?.canViewHRDocuments !== false) && (user?.role === 'admin' || user?.role === 'superadmin' || (user as any)?.permissions?.canViewHRDocuments) && (
            <TooltipWrapper title="КДП (кадровое делопроизводство)">
              <button
                onClick={() => navigate('/app/hr-docs')}
                style={getAnimatedButtonStyle('#f59e0b')}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f59e0b, #e67e22)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#f59e0b';
                  e.currentTarget.style.borderColor = '#f59e0b';
                }}
                title={showButtonText ? undefined : 'КДП'}
              >
                <FileText size={16} />
                {showButtonText && <span style={{ marginLeft: '4px' }}>КДП</span>}
              </button>
            </TooltipWrapper>
          )}

          {/* Кнопка Бухгалтерия */}
          {((user as any)?.companyModules?.canViewAccountingCRM !== false) && (user?.role === 'admin' || user?.role === 'superadmin' || (user as any)?.permissions?.canViewAccountingCRM) && (
            <TooltipWrapper title="Бухгалтерия">
              <button
                onClick={() => navigate('/app/accounting')}
                style={getAnimatedButtonStyle('#10b981')}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #10b981, #059669)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#10b981';
                  e.currentTarget.style.borderColor = '#10b981';
                }}
                title={showButtonText ? undefined : 'Бухгалтерия'}
              >
                <Wallet size={16} />
                {showButtonText && <span style={{ marginLeft: '4px' }}>Бухгалтерия</span>}
              </button>
            </TooltipWrapper>
          )}

          {/* Кнопка HR-помощник — только dev */}
          {window.location.hostname.startsWith('dev.') && (
            <TooltipWrapper title="HR-помощник">
              <button
                onClick={() => navigate('/app/hr-helper')}
                style={getAnimatedButtonStyle('#8b5cf6')}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #8b5cf6, #6a11cb)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#8b5cf6';
                  e.currentTarget.style.borderColor = '#8b5cf6';
                }}
                title={showButtonText ? undefined : 'HR-помощник'}
              >
                <Sparkles size={16} />
                {showButtonText && <span style={{ marginLeft: '4px' }}>HR-помощник</span>}
              </button>
            </TooltipWrapper>
          )}

          {/* НОВАЯ КНОПКА РАСШИРЕНИЯ CHROME */}
          <TooltipWrapper title="Расширение браузер">
            <button
              onClick={() => setShowPluginModal(true)}
              style={getAnimatedButtonStyle('#6a11cb')}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #6a11cb, #8e44ad)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = 'transparent';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6a11cb';
                e.currentTarget.style.borderColor = '#6a11cb';
              }}
              title={showButtonText ? undefined : 'Расширение браузер'}
            >
              <Download size={16} />
              {showButtonText && <span style={{ marginLeft: '4px' }}>Расширение браузер</span>}
            </button>
          </TooltipWrapper>

          <TooltipWrapper title="Обучение">
            <button
              onClick={handleElearningClick}
              style={getAnimatedButtonStyle('#9b59b6')}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #9b59b6, #8e44ad)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = 'transparent';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#9b59b6';
                e.currentTarget.style.borderColor = '#9b59b6';
              }}
              title={showButtonText ? undefined : 'Обучение'}
            >
              <GraduationCap size={16} />
              {showButtonText && <span style={{ marginLeft: '4px' }}>Обучение</span>}
            </button>
          </TooltipWrapper>

          <TooltipWrapper title="Настройки">
            <button
              onClick={() => setShowSettings(true)}
              style={{ ...getAnimatedButtonStyle('#2575fc'), position: 'relative' }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #6a11cb, #2575fc)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = 'transparent';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#2575fc';
                e.currentTarget.style.borderColor = '#2575fc';
              }}
              title={showButtonText ? undefined : 'Настройки'}
            >
              <Settings size={16} />
              {showButtonText && <span style={{ marginLeft: '4px' }}>Настройки</span>}
              {unreadTickets > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: -4,
                    right: -6,
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    borderRadius: 10,
                    padding: '1px 5px',
                    minWidth: 16,
                    textAlign: 'center',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 6px rgba(239,68,68,0.4)',
                    lineHeight: 1.4,
                    pointerEvents: 'none',
                  }}
                >
                  {unreadTickets > 99 ? '99+' : unreadTickets}
                </span>
              )}
            </button>
          </TooltipWrapper>

          <TooltipWrapper title="Выйти">
            <button
              onClick={logout}
              style={getAnimatedButtonStyle('#ff4d4f')}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #ff4d4f, #ff7875)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = 'transparent';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#ff4d4f';
                e.currentTarget.style.borderColor = '#ff4d4f';
              }}
              title={showButtonText ? undefined : 'Выйти'}
            >
              <LogOut size={16} />
              {showButtonText && <span style={{ marginLeft: '4px' }}>Выйти</span>}
            </button>
          </TooltipWrapper>
        </div>
      </header>

      {user.role === 'superadmin' && <AdminPanel />}

      <div style={styles.mainContent}>
        <TaskBoard isOpen={isTaskBoardOpen} onToggle={toggleTaskBoard} />
        <div
          style={{
            ...styles.modulesContainer,
            width: isTaskBoardOpen ? 'calc(100% - 320px)' : 'calc(100% - 100px)',
          }}
        >
          <div style={styles.modulesGrid}>
            {modules
              .filter((m) => {
                // ★ superadmin видит все модули всегда
                if (user?.role === 'superadmin') return true;

                // ★ Если модуль требует HRM — проверяем что HRM включён у компании
                if (m.requiresHRM) {
                  const cm = (user as any)?.companyModules;
                  if (!cm || cm.canViewHRM !== true) return false;
                }
                if (!m.requiresPermission) return true;
                // Admin видит модуль только если он разрешён компании
                if (user?.role === 'admin') return true;
                return (user as any)?.permissions?.[m.requiresPermission] === true;
              })
              .map((module, index) => (
                <ModuleCard
                  key={module.id}
                  title={module.title}
                  description={module.description}
                  imageSrc={module.imageSrc}
                  icon={module.icon}
                  onClick={() => handleModuleClick(module.path)}
                  backgroundColor={moduleColors[index % moduleColors.length]}
                />
              ))}
          </div>
        </div>
      </div>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      <ChatWidget />
      {showPaymentModal && <PaymentRequiredModal isOpen={showPaymentModal} onClose={handleCloseModal} />}

      {/* Модалка расширения */}
      <PluginInstallModal isOpen={showPluginModal} onClose={() => setShowPluginModal(false)} />

      <style>{`
        [data-tooltip] {
          position: relative;
        }
        [data-tooltip]:hover::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0,0,0,0.8);
          color: #fff;
          padding: 4px 10px;
          border-radius: 6px;
          white-space: nowrap;
          font-size: 12px;
          z-index: 9999;
          pointer-events: none;
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.2);
        }
      `}</style>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    position: 'relative',
    padding: '20px',
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  backgroundImage: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'url("/images/crm-background2.jpg")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    opacity: 0.15,
    zIndex: -2,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '12px 20px',
    flexShrink: 0,
    background: 'rgba(255, 255, 255, 0.4)',
    backdropFilter: 'blur(12px)',
    borderRadius: '40px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoIcon: {
    height: '36px',
    width: 'auto',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },
  logoText: {
    fontSize: '22px',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '0.5px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  userName: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#1a1a2e',
    marginRight: '8px',
    padding: '6px 12px',
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '30px',
    backdropFilter: 'blur(4px)',
  },
  tooltipContainer: {
    position: 'relative',
    display: 'inline-flex',
  },
  iconButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: '2px solid',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'all 0.25s ease',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
    backdropFilter: 'blur(4px)',
    whiteSpace: 'nowrap',
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    marginTop: '10px',
    minHeight: 0,
  },
  modulesContainer: {
    transition: 'width 0.3s ease',
    overflowY: 'auto',
    padding: '10px 15px 20px 15px',
    height: '100%',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(37, 117, 252, 0.3) transparent',
  },
  modulesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    width: '100%',
  },
  loading: {
    padding: '60px',
    textAlign: 'center' as const,
    fontSize: '18px',
    color: '#666',
    background: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(8px)',
    borderRadius: '20px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
    maxWidth: '300px',
  },
};

export default Dashboard;
